import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Alert, AlertTitle } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import { useConfirm } from "@agricircle/shared/hooks";


export const KitStatus = ({ status, farmId, kitStatusRequest, kitCancelRequest, onChange }) => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [refreshTimeout, setRefreshTimeout] = useState();

    useEffect(() => {
        if (!status) return;

        startRefreshTimeout(5000);
        return stopRefreshTimeout;
    }, [status?.jobId]);

    if (!status) return null;

    function handleRefresh() {
        startRefreshTimeout();
        kitStatusRequest(farmId, status.job_id, onChange);
    }

    function startRefreshTimeout(duration) {
        const timeout = setTimeout(() => {
            setRefreshTimeout(null);
        }, duration || 30000);
        setRefreshTimeout(timeout);
    }

    function stopRefreshTimeout() {
        setRefreshTimeout(timeout => {
            if (timeout) {
                clearTimeout(timeout);
            }
            return null;
        });
    }

    function handleCancel(noConfirm) {
        if (!status.job_id) return;
        if (noConfirm || confirm("Do you really want to cancel to background job?")) {
            stopRefreshTimeout();
            kitCancelRequest(farmId, status.job_id, () => onChange(undefined));
        }
    }

    function handleDownload() {
        window.open(status.download_url, "_blank");
        onChange(undefined);
    }

    return (<Stack direction="row" spacing={1}>
        {(!status.error && !status.completed_at || null) && (<Alert
            severity="warning"
            action={<Stack direction="row" spacing={1}>
                <Button
                    color="error"
                    data-cy="button-wait-download-cancel"
                    onClick={handleCancel}
                    size="small"
                    endIcon={<ClearIcon />}
                >
                    {t("shared:btn-cancel")}
                </Button>
                <Button
                    color="warning"
                    data-cy="button-wait-download-ok"
                    onClick={handleRefresh}
                    size="small"
                    disabled={Boolean(refreshTimeout)}
                    endIcon={<RefreshIcon sx={!refreshTimeout ? {} : {
                        animation: "spin 5s linear infinite",
                        "@keyframes spin": {
                            "0%": {
                                transform: "rotate(0deg)",
                            },
                            "100%": {
                                transform: "rotate(360deg)",
                            },
                        },
                    }} />}
                >
                    {refreshTimeout ? "" : "Re-check status"}
                </Button>
            </Stack>}
            sx={{ width: "100%" }}
        >
            {t("kit.request-in-progress")}
        </Alert>)}
        {(status.error || null) && (<Alert
            severity="error"
            action={<Button
                color="error"
                data-cy="button-wait-download-cancel"
                onClick={() => handleCancel(true)}
                size="small"
                endIcon={<ClearIcon />}
            >
                {t("shared:btn-clear")}
            </Button>}
            sx={{ width: "100%" }}
        >
            <AlertTitle>{t("kit.error-label")}</AlertTitle>
            {status.error}
        </Alert>)}
        {(!status.error && status.completed_at || null) && (<Alert
            severity="success"
            action={<Stack direction="row" spacing={1}>
                <Button
                    color="error"
                    data-cy="button-wait-download-cancel"
                    onClick={handleCancel}
                    size="small"
                    endIcon={<ClearIcon />}
                >
                    {t("shared:btn-cancel")}
                </Button>
                <Button
                    color="success"
                    onClick={handleDownload}
                    data-cy="button-wait-download-ok"
                    endIcon={<DownloadIcon />}
                    size="small"
                >
                    {t("kit.request-download-btn")}
                </Button>
            </Stack>}
            sx={{ width: "100%" }}
        >
            {t("kit.request-ready")}
        </Alert>)}
    </Stack >);
};
